import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthContext = createContext(null);

// One-shot purge of the legacy localStorage token. Older builds of Quill
// stored the session_token in localStorage (XSS-readable). The token now
// lives in an httpOnly cookie only — strip the stale value the first time
// this module loads in an existing user's browser.
if (typeof window !== "undefined") {
  try {
    window.localStorage.removeItem("cp_session_token");
  } catch {
    /* ignore */
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const userRef = useRef(null);

  const setUser = useCallback((u) => {
    userRef.current = u;
    setUserState(u);
    if (u) setSessionExpired(false);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  // A 401 on any API call while we believed the user was signed in means the
  // cookie session lapsed server-side → toast + hand off to /login.
  useEffect(() => {
    const onUnauthorized = (e) => {
      if (!userRef.current) return;
      const url = e.detail?.url || "";
      if (url.includes("/auth/login") || url.includes("/auth/session") || url.includes("/auth/logout")) return;
      userRef.current = null;
      setUserState(null);
      setSessionExpired(true);
      toast.error("Your session expired — please sign in again.", { id: "session-expired" });
    };
    window.addEventListener("quill:unauthorized", onUnauthorized);
    return () => window.removeEventListener("quill:unauthorized", onUnauthorized);
  }, []);

  useEffect(() => {
    // Skip auth fetch on public-only viewer routes (no logged-in user expected there).
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isPublicViewerRoute =
      path.startsWith("/pay/") || path.startsWith("/public/invoice/") || path.startsWith("/tax-pack");
    if (isPublicViewerRoute) {
      setLoading(false);
      return;
    }
    // CRITICAL: skip /auth/me when returning from OAuth — AuthCallback
    // will exchange the session_id and set the cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect must land on the main app route — the AppRouter detects the
    // `#session_id=...` fragment there and renders <AuthCallback /> before
    // the ProtectedRoute auth check gets a chance to redirect away.
    const redirect = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const loginWithPassword = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  };

  const signupWithPassword = async (email, password, name, referralCode, cryptoAddress) => {
    const payload = { email, password };
    if (name) payload.name = name;
    if (referralCode) payload.referral_code = referralCode;
    if (cryptoAddress) payload.crypto_address = cryptoAddress;
    const r = await api.post("/auth/signup", payload);
    setUser(r.data.user);
    return r.data.user;
  };

  const forgotPassword = async (email) => {
    await api.post("/auth/forgot-password", { email });
  };

  const resetPassword = async (token, password) => {
    const r = await api.post("/auth/reset-password", { token, password });
    setUser(r.data.user);
    return r.data.user;
  };

  const exchangeSession = async (sessionId, referralCode) => {
    // Backend sets the httpOnly cookie; we only need the user payload.
    const payload = { session_id: sessionId };
    if (referralCode) payload.referral_code = referralCode;
    const r = await api.post("/auth/session", payload);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, sessionExpired, refresh, login, logout, exchangeSession, setUser, loginWithPassword, signupWithPassword, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
